import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Textarea } from "@/components/ui/textarea"
import {
  useTranslation,
  type TranslationKeys,
} from "@/hooks/useTranslation/useTranslation"
import { useState } from "react"
import { useDeclarationQuestions } from "../../../../../api/declarationQuestionsApi"
import { formatDate } from "../../../../../utils/date"

/** A batch worth showing in the dialog, normalised from either of its two
 *  sources: the client's own read (`openQuestionBatches` over
 *  `DeclarationInquiry[]`, camelCase) or the approve 409's own
 *  `ApproveOpenQuestionBatch[]` (snake_case, arrives raw off the wire, see
 *  `approveGateTypes.ts`'s own note on why). Both collapse to this one shape
 *  so the dialog itself never has to know which source it got. */
export interface OpenQuestionBatchView {
  inquiryId: string
  status: string
  sentAt: string | null
  questionCount: number
}

export interface OpenQuestionsAckDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Whose sent questions the dialog reads (`useDeclarationQuestions(declId,
   *  true)`) to show the actual text next to each batch: the batches
   *  themselves only carry a count, not the questions. */
  declId: string
  batches: OpenQuestionBatchView[]
  /** The trimmed reason (already validated: >= 10 characters). */
  onConfirm: (reason: string) => void
  /** True while the approve request this dialog feeds is in flight: keeps
   *  confirm disabled so a second click cannot fire a second mutation. */
  pending?: boolean
}

const MIN_REASON_LENGTH = 10

/** Every OPEN status this dialog can see (contract 2): the "still waiting"
 *  statuses share one label, `ureview` (a reply landed and is being reviewed)
 *  and `undeliverable`/`complained` each get their own: a bounced address and a spam flag call for different reviewer
 *  reactions, so collapsing them the way `ApproveGatePanel`'s dead-address
 *  set does would lose that distinction here. */
function statusLabelKey(status: string): TranslationKeys {
  if (status === "undeliverable") return "MC_DECL_V2_OPENQ_STATUS_UNDELIVERABLE"
  if (status === "complained") return "MC_DECL_V2_OPENQ_STATUS_COMPLAINED"
  if (status === "ureview") return "MC_DECL_V2_OPENQ_STATUS_IN_REVIEW"
  return "MC_DECL_V2_OPENQ_STATUS_WAITING"
}

/**
 * Stage 1 (2026-09-17 decision): approving a declaration with at least one
 * open supplier question batch no longer refuses outright, it asks for an
 * explicit, reasoned acknowledgement instead. `StudioV2FooterBar` opens this
 * in place of a normal approve whenever open batches exist, both up front
 * (`openQuestionBatches` over its own `useDeclarationInquiries` read) and on
 * the server's own late-arriving 409 `OPEN_QUESTIONS_UNACKNOWLEDGED`.
 *
 * Dialog pattern borrowed verbatim from `RequestQuestionsDialog`: `sv2-root`
 * re-declared on the portaled content so the workspace's scoped tokens still
 * resolve outside it, and `onEscapeKeyDown` stopped from bubbling so Escape
 * never also reaches `StudioV2Layout`'s window-level back-to-list handler.
 */
export function OpenQuestionsAckDialog({
  open,
  onOpenChange,
  declId,
  batches,
  onConfirm,
  pending = false,
}: OpenQuestionsAckDialogProps) {
  const { translate } = useTranslation()
  const { data: sentQuestions } = useDeclarationQuestions(declId, true)
  const [reason, setReason] = useState("")

  const batchIds = new Set(batches.map((b) => b.inquiryId))
  const items = (sentQuestions ?? []).filter(
    (q) => q.inquiryId != null && batchIds.has(q.inquiryId),
  )
  // The question texts have not loaded yet (or none resolved): fall back to
  // the batches' own counts rather than showing "(0)" while the real number
  // is already known server-side.
  const count =
    items.length > 0
      ? items.length
      : batches.reduce((sum, b) => sum + b.questionCount, 0)

  const trimmedReason = reason.trim()
  const canConfirm = trimmedReason.length >= MIN_REASON_LENGTH && !pending

  const handleConfirm = () => {
    if (!canConfirm) return
    onConfirm(trimmedReason)
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) setReason("")
        onOpenChange(next)
      }}
    >
      <DialogContent
        data-testid="open-questions-ack-dialog"
        className="sv2-root flex max-h-[85vh] w-[560px] max-w-[95vw] flex-col gap-0 p-0"
        onEscapeKeyDown={(e) => e.stopPropagation()}
      >
        <DialogHeader className="border-b p-4">
          <DialogTitle>
            {translate("MC_DECL_V2_OPENQ_ACK_TITLE", { count })}
          </DialogTitle>
          <DialogDescription>
            {translate("MC_DECL_V2_OPENQ_ACK_INTRO")}
          </DialogDescription>
        </DialogHeader>
        <div className="flex flex-col gap-3 overflow-y-auto p-4">
          {/* Grouped by batch (sent date + status shown ONCE per batch, not
              once per question: a batch commonly carries several questions,
              and repeating its own metadata on every one of them would just
              be noise). Falls back to the bare batch summary when the texts
              have not resolved yet (`items` empty), same fallback the count
              above uses. */}
          {batches.map((batch) => {
            const batchItems = items.filter(
              (q) => q.inquiryId === batch.inquiryId,
            )
            return (
              <div
                key={batch.inquiryId}
                data-testid={`openq-batch-${batch.inquiryId}`}
                className="flex flex-col gap-2 rounded-md border p-3 text-[13px]"
              >
                <div className="flex items-center gap-2 text-[11px] text-grey5">
                  <span>
                    {translate("MC_DECL_V2_OPENQ_SENT_AT", {
                      date: formatDate(batch.sentAt) || "—",
                    })}
                  </span>
                  <span>·</span>
                  <span>{translate(statusLabelKey(batch.status))}</span>
                </div>
                {batchItems.length > 0 ? (
                  <ul className="flex flex-col gap-1">
                    {batchItems.map((q) => (
                      <li key={q.id} data-testid={`openq-item-${q.id}`}>
                        {q.question}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-grey5">
                    {translate("MC_DECL_V2_OPENQ_BATCH_COUNT", {
                      n: batch.questionCount,
                    })}
                  </p>
                )}
              </div>
            )
          })}
        </div>
        <div className="flex flex-col gap-1.5 border-t p-4">
          <label htmlFor="openq-ack-reason" className="sv2-seclbl">
            {translate("MC_DECL_V2_OPENQ_ACK_REASON_LABEL")}
          </label>
          <Textarea
            id="openq-ack-reason"
            data-testid="openq-ack-reason"
            rows={3}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
          />
          <p className="text-[11px] text-grey5">
            {translate("MC_DECL_V2_OPENQ_ACK_REASON_HINT")}
          </p>
        </div>
        <DialogFooter className="border-t p-4">
          <Button
            type="button"
            variant="outline"
            data-testid="openq-ack-cancel"
            onClick={() => {
              setReason("")
              onOpenChange(false)
            }}
          >
            {translate("MC_DECL_V2_OPENQ_ACK_CANCEL")}
          </Button>
          <Button
            type="button"
            data-testid="openq-ack-confirm"
            disabled={!canConfirm}
            onClick={handleConfirm}
          >
            {translate("MC_DECL_V2_OPENQ_ACK_CONFIRM")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
